import { type FC, useEffect } from 'react'

import { AnswerCall } from '@liveHelper/assets/AnswerCall'
import { Minimize } from '@liveHelper/assets/Minimize'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { PrivacyAlert } from '@liveHelper/components/PrivacyAlert'
import { Row } from '@liveHelper/components/shared/styles'
import { useAppointment } from '@liveHelper/context/AppointmentContext/AppointmentContext'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useAcceptCameraInvite } from '@liveHelper/hooks/useAcceptCameraInvite'
import { useConversationAction } from '@liveHelper/hooks/useConversationAction'
import { useFileUpload } from '@liveHelper/hooks/useFileUpload'
import { useInitiateCall } from '@liveHelper/hooks/useInitiateCall'
import { useIsActivePreviousConversation } from '@liveHelper/hooks/useIsActiveConversation'
import { useLoadMoreMessages } from '@liveHelper/hooks/useLoadMoreMessages'
import { useVisitorCanUseCamera } from '@liveHelper/hooks/useVisitorCanUseCamera'
import { selectors } from '@liveHelper/models/modal/modalRegistry'
import {
  actionsSelector,
  conversationSelector,
  isAgentTypingSelector,
  isWaitingForVisitorInputSelector,
  liveHelperErrorsSelector,
  messagesSelector,
  paramsSelector,
  uiStateSelector,
  useStore,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { useGetUtmContent } from '@liveHelper/utils/useGetUtmContent'
import { Close } from '@oneToOne/shared/assets/Close'
import { ChatContainer } from '@oneToOne/shared/components/chat/ChatContainer'
import {
  DEFAULT_TYPING_INDICATOR_THROTTLE_DELAY,
  TYPING_MESSAGE,
} from '@oneToOne/shared/components/chat/constants'
import { SystemErrorLazy } from '@oneToOne/shared/components/systemError'
import { ChatHeader } from '@oneToOne/shared/components/twind/ChatHeader'
import { ChatInput } from '@oneToOne/shared/components/twind/ChatInput'
import { ActionButton } from '@oneToOne/shared/styles'
//import { useTarget } from '@oneToOne/shared/contexts/TargetContext'
import type { IAgoraRTCRemoteUser, ILocalVideoTrack } from 'agora-rtc-sdk-ng'
import { throttle } from 'es-toolkit'

//import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import { useContextTranslation } from '@core/i18n'
import { isMobileWeb } from '@core/userAgent'

import { END_CONVERSATION_CONFIRM_MODAL } from '../OneToOneModal/modalIds'

import { ChatHeaderDialing } from './ChatHeaderDialing'
import { ChatHeaderOnCall } from './ChatHeaderOnCall'
import { ChatHeaderRinging } from './ChatHeaderRinging'
import { ChattingWrapper } from './Chatting.styles'

interface Props {
  agentRTCUser: IAgoraRTCRemoteUser | undefined
  localVideoTrack: ILocalVideoTrack | undefined
}

/**
 * Minimized Chatting Component
 */
export const Chatting: FC<Props> = ({ agentRTCUser, localVideoTrack }) => {
  const chatChannelConfig = useChatChannelConfig()
  const { t } = useContextTranslation()
  const params = useStore(paramsSelector)
  //const liveHelperShadowRoot = useTarget()
  const { endConversation, sendTextMessage, sendTypingActivity } = useStore(
    visitorPrivateActionsSelector,
  )
  const { showModal, hideModal } = useStore(selectors.modalActionsSelector)
  const uiState = useStore(uiStateSelector)
  const messages = useStore(messagesSelector)
  const conversation = useStore(conversationSelector)
  const liveHelperErrors = useStore(liveHelperErrorsSelector)
  const isAgentTyping = useStore(isAgentTypingSelector)
  const isWaitingForVisitorInput = useStore(isWaitingForVisitorInputSelector)
  const isTextOnlyAgent = Boolean(
    conversation?.agent?.available_request_type === 'REQUEST_FILTER_TYPE_TEXT',
  )
  const { appointment } = useAppointment()

  //const isMobileWebEnabled = isMobileWeb()

  const initiateCall = useInitiateCall({})

  const {
    sendUIStateTransitionEvent,
    removeLiveHelperError,
    showFullScreenPreview,
    declineCameraInvite,
  } = useStore(actionsSelector)

  const visitorCanUseCamera = useVisitorCanUseCamera()
  const { markLatestMessageAsRead } = useMessagePreviewContext()

  const handleFileChange = useFileUpload()

  // Hide the confirm modal when this component is unmounted.
  useEffect(() => {
    return () => {
      hideModal(END_CONVERSATION_CONFIRM_MODAL)
    }
  }, [hideModal])

  useEffect(() => {
    markLatestMessageAsRead()
  }, [markLatestMessageAsRead])

  const loadMoreMessages = useLoadMoreMessages()
  const utmContent = useGetUtmContent()
  const isActivePreviousConversation = useIsActivePreviousConversation()
  const { onEndConversationConversationAction, onJoinQueueConversationAction } =
    useConversationAction()
  const onAcceptCameraInvite = useAcceptCameraInvite()

  // Hook to disable body scroll when the chat is open
  // This is needed for IOS mobile web to prevent the page from scrolling.
  // [OTO-1065]: Re-enable body scroll lock
  // useEffect(() => {
  //   let virtuosoScroller: Element | null = null
  //   if (liveHelperShadowRoot && isMobileWebEnabled) {
  //     virtuosoScroller = liveHelperShadowRoot.querySelector(
  //       '[data-test-id="virtuoso-scroller"]',
  //     )
  //     disableBodyScroll(virtuosoScroller)
  //   }
  //   // Pass the only element that will be allowed to scroll: the virtuoso scroller.
  //   return () => clearAllBodyScrollLocks()
  // }, [liveHelperShadowRoot, isMobileWebEnabled])

  const canInteractWithAgent =
    !appointment || (appointment && appointment.status !== 'expired')

  const throttleTypingActivityHandler = throttle(
    sendTypingActivity || (() => undefined),
    DEFAULT_TYPING_INDICATOR_THROTTLE_DELAY,
    { edges: ['leading'] },
  )

  return (
    <ChattingWrapper isMobileWeb={isMobileWeb()}>
      <ChatContainer
        enableLink={true}
        lang={params.lang}
        utmContent={utmContent}
        messages={isAgentTyping ? [...messages, TYPING_MESSAGE] : messages}
        loadMoreMessages={loadMoreMessages}
        chatInput={
          !isWaitingForVisitorInput && canInteractWithAgent
            ? () => (
                <ChatInput
                  onEnter={sendTextMessage}
                  typingActivityHandler={throttleTypingActivityHandler}
                  handleFileChange={handleFileChange}
                />
              )
            : undefined
        }
        onSendMessage={(payload) =>
          sendTextMessage?.(payload.message, payload.isConversationAction)
        }
        onEndConversation={onEndConversationConversationAction}
        onJoinQueue={onJoinQueueConversationAction}
        renderChatErrors={() => (
          <SystemErrorLazy
            errors={liveHelperErrors}
            onDismiss={removeLiveHelperError}
          />
        )}
        renderChatOverlay={() => <PrivacyAlert />}
        renderChatHeader={() =>
          uiState === 'chatting_ringing' ? (
            <ChatHeaderRinging agentRTCUser={agentRTCUser} />
          ) : uiState === 'chatting_dialing' ? (
            <ChatHeaderDialing />
          ) : uiState === 'chatting_on_call' ? (
            <ChatHeaderOnCall
              agentRTCUser={agentRTCUser}
              localVideoTrack={localVideoTrack}
            />
          ) : (
            <ChatHeader
              title={t(`oneToOne:Chat`)}
              actions={
                <Row>
                  {!isWaitingForVisitorInput &&
                  canInteractWithAgent &&
                  !isTextOnlyAgent ? (
                    <ActionButton
                      aria-label={t(`oneToOne:Call Agent`)}
                      background="var(--primary-light)"
                      buttonSize="sm"
                      shape="square"
                      onClick={() => initiateCall()}
                      icon={
                        <AnswerCall
                          width="12px"
                          fill="var(--primary)"
                          label=""
                          aria-hidden
                        />
                      }
                    />
                  ) : null}
                  {conversation ? (
                    <ActionButton
                      aria-label={t(`oneToOne:Minimize Widget`)}
                      background="var(--white)"
                      buttonSize="sm"
                      shape="square"
                      onClick={() => {
                        sendUIStateTransitionEvent('MINIMIZE')
                      }}
                      icon={
                        <Minimize
                          width="16px"
                          fill="var(--gray-9)"
                          label=""
                          aria-hidden
                        />
                      }
                    />
                  ) : null}
                  {!appointment || !conversation ? (
                    <ActionButton
                      shape="square"
                      aria-label={t(`oneToOne:End Conversation`)}
                      background="var(--white)"
                      buttonSize="sm"
                      onClick={() => {
                        if (conversation) {
                          showModal(
                            END_CONVERSATION_CONFIRM_MODAL,
                            'endConversationConfirm',
                            { endConversation },
                          )
                        } else {
                          sendUIStateTransitionEvent({
                            type: 'END_CONVERSATION',
                            isAppointment: !!appointment,
                            isActiveConversation: isActivePreviousConversation,
                          })
                        }
                      }}
                      icon={
                        <Close
                          width="12px"
                          fill="var(--gray-9)"
                          label=""
                          aria-hidden
                        />
                      }
                    />
                  ) : null}
                </Row>
              }
            />
          )
        }
        displayFWLogo={
          !!chatChannelConfig?.widget_style?.firework_branding &&
          uiState !== 'on_call_fullscreen'
        }
        showFullScreenPreview={showFullScreenPreview}
        pipParams={params}
        isCameraInviteActive={visitorCanUseCamera}
        onDeclineCameraInvite={declineCameraInvite}
        onAcceptCameraInvite={onAcceptCameraInvite}
      />
    </ChattingWrapper>
  )
}
